// src/features/role-profiles/components/RoleProfileForm.tsx

import React, { useEffect } from 'react';
import { Modal, Form, Input, Switch, Button, Space, message, Spin } from 'antd';
import { useAppTranslation } from '../../../hooks/useAppTranslation';
import { RoleProfile, RolePermissions } from '../../../types/user';
import { useAddRoleProfile, useUpdateRoleProfile } from '../../../hooks/useRoleProfiles';
import { useBusiness } from '../../../hooks/useBusinesses';
import RoleProfilePermissionsEditor from './RoleProfilePermissionsEditor';
import { normalizeRolePermissions } from '../../../types/user';
import { useAuth } from '../../../contexts/AuthContext';
import {
  canCreateRoleProfiles,
  getRoleProfilesFeatureGateMessage,
  stripInactiveFeaturePermissions,
} from '../../../utils/roleProfileFeatureSync';

const { TextArea } = Input;

interface RoleProfileFormProps {
  visible: boolean;
  profile: RoleProfile | null;
  onSuccess: () => void;
  onCancel: () => void;
  /** Business cible (Super Admin). Sinon = business connecté. */
  businessId?: string;
}

const RoleProfileForm: React.FC<RoleProfileFormProps> = ({
  visible,
  profile,
  onSuccess,
  onCancel,
  businessId: businessIdProp,
}) => {
  const { t } = useAppTranslation(['settings', 'common']);
  const [form] = Form.useForm();
  const isEditMode = Boolean(profile);
  const { currentBusiness } = useAuth();
  const resolvedBusinessId = businessIdProp || currentBusiness?.id || '';
  const { data: business, isLoading: businessLoading } = useBusiness(resolvedBusinessId);

  const addMutation = useAddRoleProfile(businessIdProp);
  const updateMutation = useUpdateRoleProfile();

  useEffect(() => {
    if (!visible || businessLoading) return;

    if (isEditMode && profile) {
      form.setFieldsValue({
        name: profile.name,
        description: profile.description,
        permissions: stripInactiveFeaturePermissions(
          normalizeRolePermissions(profile.permissions),
          business ?? null
        ),
        isActive: profile.isActive,
        isDefault: profile.isDefault,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        permissions: normalizeRolePermissions(undefined),
        isActive: true,
        isDefault: false,
      });
    }
  }, [visible, isEditMode, profile, form, business, businessLoading]);

  const handleSubmit = async () => {
    try {
      if (!business) {
        message.error(t('roleProfiles.businessMissing'));
        return;
      }
      if (!isEditMode && !canCreateRoleProfiles(business)) {
        message.error(getRoleProfilesFeatureGateMessage(business));
        return;
      }

      const values = await form.validateFields();
      const permissions = stripInactiveFeaturePermissions(values.permissions, business);

      if (isEditMode && profile) {
        await updateMutation.mutateAsync({
          id: profile.id,
          name: values.name,
          description: values.description,
          permissions,
          isActive: values.isActive,
        });
      } else {
        await addMutation.mutateAsync({
          name: values.name,
          description: values.description,
          permissions,
          isActive: values.isActive,
          isDefault: values.isDefault,
        });
      }

      onSuccess();
    } catch (error: any) {
      console.error('Erreur formulaire profil:', error);
      message.error(error.message || t('roleProfiles.form.saveError'));
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handlePermissionsChange = (permissions: RolePermissions) => {
    form.setFieldsValue({ permissions });
  };

  return (
    <Modal
      title={
        <Space>
          {isEditMode ? t('roleProfiles.form.editTitle') : t('roleProfiles.form.createTitle')}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t('common:actions.cancel')}
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={addMutation.isPending || updateMutation.isPending}
          disabled={businessLoading || !business}
        >
          {isEditMode ? t('common:actions.save') : t('common:actions.create')}
        </Button>,
      ]}
      destroyOnClose
    >
      {businessLoading || !business ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin tip={t('roleProfiles.form.loadingFeatures')} />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            permissions: normalizeRolePermissions(undefined),
            isActive: true,
            isDefault: false,
          }}
        >
          <Form.Item
            name="name"
            label={t('roleProfiles.form.nameLabel')}
            rules={[
              { required: true, message: t('roleProfiles.form.nameRequired') },
              { min: 3, message: t('roleProfiles.form.nameMin') },
              { max: 50, message: t('roleProfiles.form.nameMax') },
            ]}
          >
            <Input placeholder={t('roleProfiles.form.namePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('roleProfiles.form.descriptionLabel')}
            rules={[
              { max: 200, message: t('roleProfiles.form.descriptionMax') },
            ]}
          >
            <TextArea
              rows={3}
              placeholder={t('roleProfiles.form.descriptionPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            name="permissions"
            label={t('roleProfiles.editor.title')}
            rules={[
              { required: true, message: t('roleProfiles.form.permissionsRequired') },
            ]}
          >
            <RoleProfilePermissionsEditor
              onChange={handlePermissionsChange}
              business={business}
            />
          </Form.Item>

          <Form.Item
            name="isActive"
            label={t('roleProfiles.form.statusLabel')}
            valuePropName="checked"
          >
            <Switch
              checkedChildren={t('roleProfiles.form.activeSwitch')}
              unCheckedChildren={t('roleProfiles.form.inactiveSwitch')}
            />
          </Form.Item>

          {!isEditMode && (
            <Form.Item
              name="isDefault"
              label={t('roleProfiles.form.defaultProfileLabel')}
              valuePropName="checked"
              tooltip={t('roleProfiles.form.defaultProfileTooltip')}
            >
              <Switch
                checkedChildren={t('roleProfiles.form.yes')}
                unCheckedChildren={t('roleProfiles.form.no')}
              />
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default RoleProfileForm;
